import React, { useState, useRef, useEffect } from "react";
import { Alert, Box, Button, Divider, Typography } from "@mui/material";
import IncomeImage from "../../assets/images/income.png";
import { Input } from "../Input";
import { IncomeSource } from "./IncomeSource";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DocumentUpload } from "../DocumentUpload";
import { get, post } from "../../api/";
import { query, save, deleteRecord } from "../../services/object";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { PlaidIncomeLink } from "../../components/PlaidIncomeLink";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";

const AddIncome = ({ userDetails, income, onAddIncomeComplete, closeModal, editMode, selfEmployedIncomeModalOpen }) => {
    const [incomeDetail, setIncomeDetail] = useState(income ? income : {});
    const [currentScreenIndex, setCurrentScreenIndex] = useState(editMode ? 1 : 0);
    const [documentToUpload, setDocumentToUpload] = useState();
    const [manualIncome, setManualIncome] = useState(false);
    const [documentUploadDoneLoading, setDocumentUploadDoneLoading] = useState(false);
    const [plaidButtonLoadingDone, setPlaidButtonLoadingDone] = useState(false);

    const incomeSourceRef = useRef();
    const uploadDocumentRef = useRef();

    useEffect(() => {
        setIncomeDetail(income ? income : {});
        if (income?.errorMessage) {
            setTimeout(() => {
                setIncomeDetail({
                    ...incomeDetail,
                    ...income,
                    errorMessage: "",
                });
            }, 15 * 1000);
        }
    }, [income]);

    useEffect(() => {
        if (editMode) {
            getDocuments();
        }
    }, []);

    const getDocuments = () => {
        if (incomeDetail.id) {
            const result = query("document", "getRelatedRecords", {
                parentId: incomeDetail.id,
            }).then((result) => {
                if (result.isSuccess) {
                    let documentData = result.data;
                    let income = { ...incomeDetail };
                    income.documents = documentData;
                    setIncomeDetail(income);
                }
            });
        }
    };

    const incomeTypeOptions = [
        {
            id: "Traditional Employment",
            value: "Traditional Employment",
            helpText: "",
            documents:
                incomeDetail?.frequency === "Weekly"
                    ? [
                          {
                              id: "Most recent paystub 1 - Traditional Employment",
                              label: "Most recent paystub 1",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                          {
                              id: "Most recent paystub 2 - Traditional Employment",
                              label: "Most recent paystub 2",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                          {
                              id: "Most recent paystub 3 - Traditional Employment",
                              label: "Most recent paystub 3",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                          {
                              id: "Most recent paystub 4 - Traditional Employment",
                              label: "Most recent paystub 4",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                      ]
                    : [
                          {
                              id: "Most recent paystub 1 - Traditional Employment",
                              label: "Most recent paystub 1",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                          {
                              id: "Most recent paystub 2 - Traditional Employment",
                              label: "Most recent paystub 2",
                              required: incomeDetail?.requiredDocument || !incomeDetail?.plaidIncomeVerified,
                          },
                          // { id: "W2 - Traditional Employment", label: "W2", required: false },
                          // {
                          //     id: "Most Recent Tax Return - Traditional Employment",
                          //     label: "Most Recent Tax Return",
                          //     required: false,
                          // },
                          // {
                          //     id: "Recent Bank Statement 1 - Traditional Employment",
                          //     label: "Recent Bank Statement 1",
                          //     required: false,
                          // },
                          // {
                          //     id: "Recent Bank Statement 2 - Traditional Employment",
                          //     label: "Recent Bank Statement 2",
                          //     required: false,
                          // },
                          // {
                          //     id: "Recent Paypal or Venmo Statement 1 - Traditional Employment",
                          //     label: "Recent Paypal or Venmo Statement 1",
                          //     required: false,
                          // },
                          // {
                          //     id: "Recent Paypal or Venmo Statement 2 - Traditional Employment",
                          //     label: "Recent Paypal or Venmo Statement 2",
                          //     required: false,
                          // },
                      ],
            docsRequired: 2,
        },
        {
            id: "Self Employed",
            value: "Self Employed",
            helpText:
                "For self employed applicants you'll need to provide 2 or more of the following documents. The more you can provide the better!",
            documents: [
                {
                    id: "Most Recent Tax Return - Self Employed",
                    label: "Most Recent Tax Return",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 1 - Self Employed",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Self Employed",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Proof of Business Ownership - Self Employed",
                    label: "Proof of Business Ownership",
                    required: false,
                },
                {
                    id: "Business Profit and Loss Statement - Self Employed",
                    label: "Business Profit and Loss Statement",
                    required: false,
                },
                {
                    id: "Recent Paypal or Venmo Statement 1 - Self Employed",
                    label: "Recent Paypal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent Paypal or Venmo Statement 2 - Self Employed",
                    label: "Recent Paypal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },

        {
            id: "Independent Contractor",
            value: "Independent Contractor",
            helpText:
                "For applicants who are independent contractors you'll need to provide 2 or more of the following documents. The more you can provide the better!",
            documents: [
                {
                    id: "Most Recent Tax Return - Independent Contractor",
                    label: "Most Recent Tax Return",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 1 - Independent Contractor",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Independent Contractor",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent Paypal or Venmo Statement 1 - Independent Contractor",
                    label: "Recent Paypal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent Paypal or Venmo Statement 2 - Independent Contractor",
                    label: "Recent Paypal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
        {
            id: "Social Security / VA Disability",
            value: "Social Security / VA Disability",
            helpText:
                "For Social Security or VA disability benefits please upload 2 or more of the following documents!",
            documents: [
                {
                    id: "Award Letter / Benefit Statement - Social Security / VA Disability",
                    label: "Award Letter / Benefit Statement",
                    required: true,
                },
                {
                    id: "Recent Bank Statement 1 - Social Security / VA Disability",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Social Security / VA Disability",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 1 - Social Security / VA Disability",
                    label: "Recent PayPal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 2 - Social Security / VA Disability",
                    label: "Recent PayPal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
        {
            id: "Child Support Alimony",
            value: "Child Support Alimony",
            helpText: "For child support or alimnoy, please provide bank or venmo statements!",
            documents: [
                {
                    id: "Court Documents - Child Support Alimony",
                    label: "Court Documents",
                    required: true,
                },
                {
                    id: "Recent Bank Statement 1 - Child Support Alimony",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Child Support Alimony",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 1 - Child Support Alimony",
                    label: "Recent PayPal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 2 - Child Support Alimony",
                    label: "Recent PayPal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
        {
            id: "Military Benefits",
            value: "Military Benefits",
            helpText: "For military benefits, please provide paystubs for the past 60 days!",
            documents: [
                {
                    id: "Recent Benefits Statement - Military Benefits",
                    label: "Recent Benefits Statement",
                    required: true,
                },
                {
                    id: "Recent Bank Statement 1 - Military Benefits",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Military Benefits",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 1 - Military Benefits",
                    label: "Recent PayPal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 2 - Military Benefits",
                    label: "Recent PayPal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
        {
            id: "Annuity / Retirement / Pension",
            value: "Annuity / Retirement / Pension",
            helpText: "For Annuity, Retirement, or Pension income sources please provice 2 recent bank statements!",
            documents: [
                {
                    id: "Award Letter / Benfit Statement - Annuity / Retirement / Pension",
                    label: "Award Letter / Benfit Statement",
                    required: true,
                },
                {
                    id: "Recent Bank Statement 1 - Annuity / Retirement / Pension",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Annuity / Retirement / Pension",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 1 - Annuity / Retirement / Pension",
                    label: "Recent PayPal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 2 - Annuity / Retirement / Pension",
                    label: "Recent PayPal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
        {
            id: "Other Income Source",
            value: "Other Income Source",
            helpText: "For other income sources, please provide recent bank statements to verify your income!",
            documents: [
                {
                    id: "Most recent tax return - Other Income Source",
                    label: "Most recent tax return",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 1 - Other Income Source",
                    label: "Recent Bank Statement 1",
                    required: false,
                },
                {
                    id: "Recent Bank Statement 2 - Other Income Source",
                    label: "Recent Bank Statement 2",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 1 - Other Income Source",
                    label: "Recent PayPal or Venmo Statement 1",
                    required: false,
                },
                {
                    id: "Recent PayPal or Venmo Statement 2 - Other Income Source",
                    label: "Recent PayPal or Venmo Statement 2",
                    required: false,
                },
            ],
            docsRequired: 2,
        },
    ];

    const handleIncomeTypeChange = (value) => {
        setIncomeDetail({ incomeType: value });
        setCurrentScreenIndex(1);
        const detailEvent = new CustomEvent("income-screen-update", {
            detail: { value: true },
        });
        document.dispatchEvent(detailEvent);
        setManualIncome(false);
        setPlaidButtonLoadingDone(false);
    };

    const handleNextIncomeDetailsClick = () => {
        if (incomeSourceRef?.current?.validate()) {
            if (incomeDetail.incomeType === "Self Employed") {
                closeModal();
                selfEmployedIncomeModalOpen(incomeDetail);
            } else {
                setCurrentScreenIndex(2);
            }
        }
    };

    const hasDocumentUploaded = (document) => {
        if (document) {
            return incomeDetail?.documents?.find(
                (item) => item.name + ` - ${incomeDetail?.incomeType}` === document.id && !item.isDeleted
            );
        }
        return false;
    };

    const handleDocumentUploadClick = (document) => {
        if (!hasDocumentUploaded(document)) {
            setCurrentScreenIndex(3);
            setDocumentToUpload(document);
            // setTimeout(() => {
            //     uploadDocumentRef?.current?.uploadFile();
            // }, 1000);
        }
    };

    const handleRefresh = (event, document) => {
        event.stopPropagation();
        setCurrentScreenIndex(3);
        setDocumentToUpload(document);
        // setTimeout(() => {
        //     uploadDocumentRef?.current?.uploadFile();
        // }, 1000);
    };

    const handleDelete = (event, document) => {
        event.stopPropagation();
        let incomeRecord = { ...incomeDetail };
        let doc = incomeRecord?.documents?.find(
            (item) => item.name + ` - ${incomeRecord?.incomeType}` === document.id && !item.isDeleted
        );

        if (doc) {
            if (doc.parentId) {
                incomeRecord.documents = incomeRecord?.documents?.map((item) => {
                    if (doc.id === item.id) {
                        item.isDeleted = true;
                    }
                    return item;
                });
            } else {
                incomeRecord.documents = incomeRecord?.documents?.filter((item) => doc.id !== item.id);
            }
            setIncomeDetail(incomeRecord);
        }
    };

    const handleDocumentUploadCancel = () => {
        setCurrentScreenIndex(2);
        setDocumentToUpload("");
    };

    const handleFileUploadComplete = (file) => {
        setCurrentScreenIndex(2);
        if (!incomeDetail.documents) {
            incomeDetail.documents = [];
        }
        let recordToUplaod = documentToUpload;
        recordToUplaod.file = file;
        recordToUplaod.name = recordToUplaod.label;
        setIncomeDetail({
            ...incomeDetail,
            documents: [...incomeDetail.documents, documentToUpload],
        });
        setDocumentToUpload("");
    };

    const getTypeDocuments = () => {
        let selectedIncomeType = incomeTypeOptions.find((item) => item.id === incomeDetail.incomeType);
        if (selectedIncomeType?.documents?.length > 0) {
            return (
                <>
                    {selectedIncomeType?.documents.map((document) => (
                        <Box
                            sx={{
                                border: "1px solid gainsboro",
                                borderRadius: "5px",
                                padding: "1rem",
                                marginBottom: "8px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignContent: "center",
                                cursor: "pointer",
                            }}
                            onClick={() => handleDocumentUploadClick(document)}
                            id={document.id.replaceAll(" ", "-").replaceAll("/", "")}>
                            <Box>
                                <Typography variant="subtitle1">
                                    {document.label} {document.required ? <span style={{ color: "red" }}>*</span> : ""}
                                </Typography>
                            </Box>
                            <Box>
                                {hasDocumentUploaded(document) && (
                                    <>
                                        <RefreshIcon
                                            onClick={(event) => handleRefresh(event, document)}
                                            sx={{ color: "#bebebe" }}></RefreshIcon>
                                        <DeleteOutlinedIcon
                                            onClick={(event) => handleDelete(event, document)}
                                            sx={{ color: "red" }}></DeleteOutlinedIcon>
                                    </>
                                )}
                                <CheckCircleOutlineIcon
                                    sx={{
                                        color: hasDocumentUploaded(document) ? "green" : "whitesmoke",
                                    }}></CheckCircleOutlineIcon>
                            </Box>
                        </Box>
                    ))}
                </>
            );
        }

        return "";
    };

    const getIncomeTypeHelpText = () => {
        let selectedIncomeType = incomeTypeOptions.find((item) => item.id === incomeDetail.incomeType);
        return selectedIncomeType?.helpText ? selectedIncomeType.helpText : "";
    };
    const getIncomeTypeUploadText = () => {
        if (incomeDetail?.incomeType === "Traditional Employment") {
            let selectedIncomeType = incomeTypeOptions.find((item) => item.id === incomeDetail?.incomeType);
            return `Please upload your ${selectedIncomeType?.documents?.length} most recent pay stubs`;
        } else {
            return `Select a document type to upload.`;
        }
    };

    const validateTypeDocument = () => {
        let hasError = false;
        let selectedIncomeType = incomeTypeOptions.find((item) => item.id === incomeDetail.incomeType);
        if (selectedIncomeType?.documents?.length > 0) {
            for (let documentRecord of selectedIncomeType.documents) {
                if (documentRecord.required && !hasDocumentUploaded(documentRecord)) {
                    hasError = true;
                    let element = document.querySelector(
                        "#" + documentRecord.id.replaceAll(" ", "-").replaceAll("/", "")
                    );
                    element.style.border = "1px solid red";
                    setTimeout(() => {
                        element.style.border = "1px solid gainsboro";
                    }, 2000);
                }
            }
        }
        return hasError;
    };

    const handleIncomeChange = (value) => {
        if (value) {
            setIncomeDetail({ ...incomeDetail, ...value });
            setPlaidButtonLoadingDone(false);
        }
    };

    const handleDocumentUploadDone = async () => {
        if (!validateTypeDocument()) {
            setDocumentUploadDoneLoading(true);
            let result = await post("income/addIncomeDetails", {
                applicantId: userDetails?.applicant?.id,
                income: incomeDetail,
            });
            if (result.isSuccess) {
                let documentRecords = incomeDetail.documents?.map((item) => {
                    if (item.parentId) {
                        return item;
                    }
                    return {
                        ...item.file,
                        parentId: result.data.id,
                        name: item.label,
                    };
                });

                let documentResult = await save("document", documentRecords);
                onAddIncomeComplete(result.data, editMode);
                setDocumentUploadDoneLoading(false);
            }
        }
    };

    const handlePlaidClick = () => {
        closeModal();
    };
    const handleCloseModal = () => {
        closeModal();
    };

    const handleCloseError = () => {
        setIncomeDetail({ ...incomeDetail, errorMessage: "" });
    };

    const handleManuallyDefine = () => {
        setManualIncome(true);
    };

    const getIncomeScreens = () => {
        console.log("incomeDetail", incomeDetail);
        const incomeScreen = [
            {
                id: "screen1",
                body: (
                    <Box
                        sx={{
                            mt: "3rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}>
                        {/* <img src={IncomeImage} alt="error" style={{ width: "100px" }}></img> */}
                        <Typography variant="h1" component="div" sx={{ mt: "1rem", textAlign: "center" }}>
                            <b>Add your income sources</b>
                        </Typography>
                        <Typography variant="h6" component="div" sx={{ mt: ".5rem", textAlign: "center" }}>
                            Choose income type from the dropdown below
                        </Typography>
                        <Box sx={{ mt: "2rem", width: "100%" }}>
                            <Input
                                type="select"
                                id={"incomeType"}
                                placeholder={"Income Type"}
                                label={""}
                                fullWidth
                                //value={incomeDetail.incomeType}
                                options={incomeTypeOptions}
                                required
                                isRequired={true}
                                onChange={(value) => handleIncomeTypeChange(value)}
                            />
                        </Box>
                    </Box>
                ),
                refs: [],
            },
            {
                id: "screen2",
                body: (
                    <Box sx={{ mt: "1rem", width: "90%" }}>
                        {incomeDetail?.errorMessage && (
                            <Alert
                                severity="error"
                                sx={{ display: "flex !important", mb: "1rem" }}
                                onClose={handleCloseError}>
                                {incomeDetail?.errorMessage}
                            </Alert>
                        )}
                        <Typography variant="h1" component="div" sx={{ textAlign: "center", marginBottom: "10px" }}>
                            <>
                                {incomeDetail.incomeType === "Traditional Employment" && !editMode && !manualIncome ? (
                                    <b>Verify Your Income</b>
                                ) : (
                                    <b>Provide Income Details</b>
                                )}
                            </>
                        </Typography>
                        <Typography variant="body1" component="div" sx={{ textAlign: "center" }}>
                            {incomeDetail.incomeType === "Traditional Employment" && !editMode && !manualIncome ? (
                                <>Choose one of the following options:</>
                            ) : (
                                <>
                                    Please provide the following details for your {incomeDetail.incomeType} income
                                    source.
                                </>
                            )}
                        </Typography>
                        <Box sx={{ mt: "1rem", width: "100%" }}>
                            {manualIncome && (
                                <IncomeSource
                                    singleIncome
                                    ref={incomeSourceRef}
                                    onNextIncomeClick={() => handleNextIncomeDetailsClick()}
                                    index={0}
                                    value={incomeDetail}
                                    onChange={(value) => handleIncomeChange(value)}
                                    incomeType={incomeDetail.incomeType}
                                />
                            )}
                            {!manualIncome && (
                                <>
                                    {incomeDetail.incomeType === "Traditional Employment" && !editMode ? (
                                        <>
                                            <PlaidIncomeLink
                                                onClick={handlePlaidClick}
                                                closeModal={handleCloseModal}
                                                type="digital"
                                                selectedIncomeType={incomeDetail.incomeType}
                                                label="Instant Verification"
                                                completedLoading={() =>
                                                    setPlaidButtonLoadingDone(true)
                                                }></PlaidIncomeLink>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    fontSize: "12px",
                                                    mt: "5px",
                                                }}>
                                                <span
                                                    style={{
                                                        textAlign: "center",
                                                    }}>
                                                    Securely connect to your payroll provider or employer through Plaid
                                                    to quickly share your income details.
                                                </span>
                                            </Box>
                                            {/* <Divider sx={{ mt: "1rem" }}></Divider> */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    margin: "20px 0",
                                                    fontStyle: "italic",
                                                    color: "#0D2D3A",
                                                }}>
                                                OR
                                            </Box>
                                            <LoadingButton
                                                color="primary"
                                                fullWidth
                                                variant="contained"
                                                disabled={!plaidButtonLoadingDone}
                                                sx={{
                                                    height: "45px",
                                                    backgroundColor: "#0D2D3A",
                                                }}
                                                loading={documentUploadDoneLoading}
                                                onClick={handleManuallyDefine}>
                                                <AddCircleOutlineOutlinedIcon sx={{ mr: "10px" }} color="white" />
                                                Manual Verification
                                            </LoadingButton>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    fontSize: "12px",
                                                    mt: "5px",
                                                }}>
                                                <span
                                                    style={{
                                                        textAlign: "center",
                                                    }}>
                                                    Manually enter your income details and upload paystubs. You’ll need
                                                    to connect to your bank account(s) to verify your documents.
                                                </span>
                                            </Box>
                                        </>
                                    ) : (
                                        <IncomeSource
                                            singleIncome
                                            ref={incomeSourceRef}
                                            onNextIncomeClick={() => handleNextIncomeDetailsClick()}
                                            index={0}
                                            value={incomeDetail}
                                            onChange={(value) => handleIncomeChange(value)}
                                            incomeType={incomeDetail.incomeType}
                                        />
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                ),
            },
            {
                id: "screen3",
                body: (
                    <Box sx={{ mt: "1rem", width: "100%" }}>
                        <Typography variant="h1" component="div" sx={{ textAlign: "center", mb: "10px" }}>
                            <b>Upload Proof of Income</b>
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ textAlign: "center", mb: "10px" }}>
                            {getIncomeTypeHelpText()}
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ textAlign: "center", mb: "10px" }}>
                            {getIncomeTypeUploadText()}
                        </Typography>
                        <Box sx={{ mt: "1rem", width: "100%", mb: "50px" }}>{getTypeDocuments()}</Box>
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: "0",
                                left: "0",
                                background: "white",
                                padding: "8px 8px 8px 8px",
                                width: "100%",
                                borderTop: "1px solid gainsboro",
                            }}>
                            <LoadingButton
                                color="primary"
                                fullWidth
                                variant="contained"
                                loading={documentUploadDoneLoading}
                                sx={{ height: "45px" }}
                                onClick={handleDocumentUploadDone}>
                                Done
                            </LoadingButton>
                        </Box>
                    </Box>
                ),
            },
            {
                id: "screen4",
                body: (
                    <Box sx={{ mt: "1rem", width: "100%" }}>
                        <Typography variant="h1" component="div" sx={{ textAlign: "center" }}>
                            <b>Upload Document</b>
                        </Typography>
                        <Box sx={{ my: "1rem" }}>
                            <DocumentUpload
                                ref={uploadDocumentRef}
                                parentId={""}
                                userDetails={{}}
                                onFileUploadComplete={handleFileUploadComplete}></DocumentUpload>
                        </Box>

                        {hasDocumentUploaded(documentToUpload) && (
                            <Button
                                color="secondary"
                                fullWidth
                                variant="contained"
                                sx={{
                                    height: "45px",
                                    mt: "1rem",
                                    background: "#d6d6d6",
                                    color: "black",
                                }}
                                id="next-btn"
                                onClick={handleDocumentUploadCancel}>
                                Cancel
                            </Button>
                        )}
                    </Box>
                ),
            },
        ];

        return incomeScreen;
    };

    const handleBackClick = () => {
        if (editMode) {
            if (currentScreenIndex > 1) {
                setCurrentScreenIndex(currentScreenIndex - 1);
            } else {
                closeModal();
            }
        } else {
            setCurrentScreenIndex(currentScreenIndex - 1);
        }
    };

    // if (incomeDetail?.incomeType === "Self Employed") {
    //     return (
    //         <SelfEmployed
    //             incomeDetail={incomeDetail}
    //             incomeSourceRef={incomeSourceRef}
    //             handleIncomeChange={handleIncomeChange}
    //             handleNextIncomeDetailsClick={handleNextIncomeDetailsClick}
    //         />
    //     );
    // }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
            }}>
            {currentScreenIndex !== 0 ? (
                <IconButton
                    aria-label="close"
                    onClick={handleBackClick}
                    sx={{
                        position: "absolute",
                        left: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}>
                    <KeyboardBackspaceIcon />
                </IconButton>
            ) : (
                ""
            )}
            {getIncomeScreens()[currentScreenIndex].body}
        </Box>
    );
};

export { AddIncome };
